import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  CardActionArea,
  Divider,
  Avatar,
  LinearProgress,
  Stack,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  IconButton,
  useTheme,
} from "@mui/material";

// Import MUI icons
import DashboardIcon from "@mui/icons-material/Dashboard";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import HeadsetIcon from "@mui/icons-material/Headset";
import CreateIcon from "@mui/icons-material/Create";
import PersonIcon from "@mui/icons-material/Person";
import TranslateIcon from "@mui/icons-material/Translate";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import GavelIcon from "@mui/icons-material/Gavel";

// TypeScript interfaces
interface Skill {
  skill: string;
  progress: number;
  color: string;
}

interface Achievement {
  id: number;
  title: string;
  unlocked: boolean;
}

interface Module {
  id: number;
  title: string;
  type: string;
  moduleId: string;
  progress: number;
  icon: React.ReactNode;
}

interface Deadline {
  id: number;
  title: string;
  dueDate: string;
  moduleId: string;
}

interface Activity {
  id: number;
  type: string;
  title: string;
  date: string;
  icon: React.ReactNode;
  color: string;
}

interface StudentData {
  name: string;
  program: string;
  level: string;
  avatarUrl: string;
  streak: number;
  totalPoints: number;
  overallProgress: number;
  currentWeek: number;
  totalWeeks: number;
  nextLiveClassDate: string;
  nextLiveClassTime: string;
  upcomingDeadlines: Deadline[];
  recentActivity: Activity[];
  recommendedModules: Module[];
  achievements: Achievement[];
  skillBreakdown: Skill[];
}

// Mock data for dashboard
const mockStudentData: StudentData = {
  name: "Alex Johnson",
  program: "Academic English Program",
  level: "Upper Intermediate",
  avatarUrl: "",
  streak: 7,
  totalPoints: 1250,
  overallProgress: 68,
  currentWeek: 4,
  totalWeeks: 12,
  nextLiveClassDate: "10 May 2025",
  nextLiveClassTime: "10:00 AM",
  upcomingDeadlines: [
    {
      id: 1,
      title: "Speaking Assignment",
      dueDate: "May 6",
      moduleId: "eap_yellow_week1_module1",
    },
    {
      id: 2,
      title: "Grammar Quiz",
      dueDate: "May 8",
      moduleId: "eap_yellow_week2_module3",
    },
  ],
  recentActivity: [
    {
      id: 1,
      type: "Completed",
      title: "Vocabulary Module",
      date: "Today",
      icon: <MenuBookIcon />,
      color: "success",
    },
    {
      id: 2,
      type: "Started",
      title: "Listening Exercise",
      date: "Yesterday",
      icon: <HeadsetIcon />,
      color: "primary",
    },
    {
      id: 3,
      type: "Feedback",
      title: "Writing Assignment",
      date: "2 days ago",
      icon: <CreateIcon />,
      color: "info",
    },
  ],
  recommendedModules: [
    {
      id: 1,
      title: "Academic Vocabulary",
      type: "Vocabulary",
      moduleId: "eap_yellow_week1_module2",
      progress: 25,
      icon: <TranslateIcon />,
    },
    {
      id: 2,
      title: "Essay Structure",
      type: "Writing",
      moduleId: "eap_yellow_week1_module3",
      progress: 0,
      icon: <CreateIcon />,
    },
  ],
  achievements: [
    { id: 1, title: "5-Day Streak", unlocked: true },
    { id: 2, title: "Perfect Score", unlocked: true },
    { id: 3, title: "Fast Learner", unlocked: false },
  ],
  skillBreakdown: [
    { skill: "Reading", progress: 75, color: "#3f51b5" },
    { skill: "Writing", progress: 60, color: "#f44336" },
    { skill: "Listening", progress: 80, color: "#4caf50" },
    { skill: "Speaking", progress: 55, color: "#ff9800" },
    { skill: "Grammar", progress: 70, color: "#9c27b0" },
  ],
};

// Map module types to icons
const getModuleIcon = (type: string): React.ReactNode => {
  switch (type) {
    case "Reading":
      return <MenuBookIcon />;
    case "Listening":
      return <HeadsetIcon />;
    case "Speaking":
      return <RecordVoiceOverIcon />;
    case "Writing":
      return <CreateIcon />;
    case "Vocabulary":
      return <TranslateIcon />;
    case "Grammar":
      return <GavelIcon />;
    default:
      return <MenuBookIcon />;
  }
};

const StudentDashboard: React.FC = () => {
  const theme = useTheme();
  const [studentData, setStudentData] = useState<StudentData>(mockStudentData);

  // This would be replaced with actual API calls in a real implementation
  useEffect(() => {
    // Example of how you would fetch data in a real implementation
    // const fetchStudentData = async () => {
    //   try {
    //     const response = await fetch('/api/student/dashboard');
    //     const data: StudentData = await response.json();
    //     setStudentData(data);
    //   } catch (error) {
    //     console.error('Error fetching student data:', error);
    //   }
    // };
    //
    // fetchStudentData();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Welcome Header with Overview */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: "white",
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={7}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{ width: 80, height: 80, bgcolor: "white" }}
                alt={studentData.name}
              >
                <PersonIcon
                  sx={{ fontSize: 40, color: theme.palette.primary.main }}
                />
              </Avatar>
              <Box>
                <Typography variant="h4" component="h1" gutterBottom>
                  Welcome back, {studentData.name}!
                </Typography>
                <Typography variant="body1">
                  {studentData.program} • Level: {studentData.level}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-around",
                alignItems: "center",
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                  {studentData.streak}
                </Typography>
                <Typography variant="body2">Day Streak</Typography>
              </Box>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
              />
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                  {studentData.totalPoints}
                </Typography>
                <Typography variant="body2">Total Points</Typography>
              </Box>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
              />
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                  {studentData.overallProgress}%
                </Typography>
                <Typography variant="body2">Progress</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* Left column */}
        <Grid item xs={12} md={8}>
          {/* Continue Learning Section */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <MenuBookIcon sx={{ mr: 1 }} color="primary" />
                  <Typography variant="h6">Continue Learning</Typography>
                </Box>
              }
              action={
                <Button
                  component={RouterLink}
                  to="/student/my-classroom"
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                >
                  View All
                </Button>
              }
            />
            <Divider />
            <CardContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                You're on Week {studentData.currentWeek} of{" "}
                {studentData.totalWeeks}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={studentData.overallProgress}
                sx={{ height: 8, borderRadius: 4, mb: 2 }}
              />

              <Grid container spacing={2}>
                {studentData.recommendedModules.map((module) => (
                  <Grid item xs={12} sm={6} key={module.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        transition: "transform 0.2s",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 3,
                        },
                      }}
                    >
                      <CardActionArea
                        component={RouterLink}
                        to={`/student/my-classroom/module/${module.moduleId}`}
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <CardContent sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Avatar
                                sx={{
                                  bgcolor: "primary.light",
                                  width: 32,
                                  height: 32,
                                  mr: 1,
                                }}
                              >
                                {module.icon}
                              </Avatar>
                              <Chip label={module.type} size="small" />
                            </Box>
                            {module.progress > 0 && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {module.progress}% Complete
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            {module.title}
                          </Typography>
                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {module.progress > 0 ? (
                              <>
                                <AccessTimeIcon
                                  fontSize="small"
                                  sx={{ mr: 1, color: "text.secondary" }}
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Continue
                                </Typography>
                              </>
                            ) : (
                              <>
                                <PlayArrowIcon
                                  fontSize="small"
                                  sx={{ mr: 1, color: "success.main" }}
                                />
                                <Typography
                                  variant="body2"
                                  color="success.main"
                                >
                                  Start Now
                                </Typography>
                              </>
                            )}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Skills Breakdown */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <TrendingUpIcon sx={{ mr: 1 }} color="primary" />
                  <Typography variant="h6">Skills Breakdown</Typography>
                </Box>
              }
            />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                {studentData.skillBreakdown.map((skill, index) => (
                  <Grid item xs={12} key={index}>
                    <Box sx={{ mb: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">{skill.skill}</Typography>
                        <Typography variant="body2">
                          {skill.progress}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={skill.progress}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: "grey.200",
                          "& .MuiLinearProgress-bar": {
                            bgcolor: skill.color,
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card sx={{ borderRadius: 2 }}>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <AccessTimeIcon sx={{ mr: 1 }} color="primary" />
                  <Typography variant="h6">Recent Activity</Typography>
                </Box>
              }
            />
            <Divider />
            <List>
              {studentData.recentActivity.map((activity, index) => (
                <React.Fragment key={activity.id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: `${activity.color}.main` }}>
                        {activity.icon}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={activity.title}
                      secondary={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.primary"
                          >
                            {activity.type}
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            • {activity.date}
                          </Typography>
                        </Box>
                      }
                    />
                    <IconButton edge="end" aria-label="more">
                      <MoreHorizIcon />
                    </IconButton>
                  </ListItem>
                  {index < studentData.recentActivity.length - 1 && (
                    <Divider variant="inset" component="li" />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Card>
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={4}>
          {/* Next Live Class */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CalendarTodayIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Next Live Class</Typography>
              </Box>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "primary.light",
                  borderRadius: 2,
                  color: "primary.contrastText",
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {studentData.nextLiveClassDate}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {studentData.nextLiveClassTime}
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ bgcolor: "white", color: "primary.main" }}
                >
                  Join Class
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Upcoming Deadlines */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <AssignmentIcon sx={{ mr: 1 }} color="primary" />
                  <Typography variant="h6">Upcoming Deadlines</Typography>
                </Box>
              }
            />
            <Divider />
            <List sx={{ p: 0 }}>
              {studentData.upcomingDeadlines.map((deadline, index) => (
                <React.Fragment key={deadline.id}>
                  <ListItem
                    component={RouterLink}
                    to={`/student/my-classroom/module/${deadline.moduleId}`}
                    sx={{
                      textDecoration: "none",
                      color: "inherit",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <ListItemIcon>
                      <Avatar
                        sx={{ bgcolor: "warning.light", width: 36, height: 36 }}
                      >
                        <AssignmentIcon fontSize="small" />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={deadline.title}
                      secondary={`Due: ${deadline.dueDate}`}
                    />
                    <ArrowForwardIcon fontSize="small" color="action" />
                  </ListItem>
                  {index < studentData.upcomingDeadlines.length - 1 && (
                    <Divider />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Card>

          {/* Achievements */}
          <Card sx={{ borderRadius: 2 }}>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <EmojiEventsIcon sx={{ mr: 1 }} color="primary" />
                  <Typography variant="h6">Achievements</Typography>
                </Box>
              }
            />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                {studentData.achievements.map((achievement) => (
                  <Grid item xs={4} key={achievement.id}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        opacity: achievement.unlocked ? 1 : 0.5,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          mb: 1,
                          bgcolor: achievement.unlocked
                            ? "warning.main"
                            : "grey.300",
                        }}
                      >
                        <EmojiEventsIcon fontSize="large" />
                      </Avatar>
                      <Typography
                        variant="caption"
                        align="center"
                        sx={{
                          fontWeight: achievement.unlocked ? "bold" : "regular",
                        }}
                      >
                        {achievement.title}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default StudentDashboard;
